import { Component, forwardRef, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { DocumentDao } from '@app/dao/document/document.dao';
import { PrevoyanceDao } from '@app/dao/prevoyance/prevoyance.dao';
import { PREVOYANCE_TYPE_CONFIG, PrevoyanceType } from '@app/dao/prevoyance/prevoyance.types';
import { ProductDao } from '@app/dao/product/product.dao';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UserDao } from '@app/dao/user/user.dao';
import { catchError, combineLatest, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PrPrevoyanceContratBuilder } from './ea-prevoyance-contrat-form.builder';

const PRODUCT_TYPE_BY_PREVOYANCE_TYPE: Record<string, string> = {
  'prevoyance': 'CONTINGENCY',
  'assurance-emprunteur': 'BORROWER_INSURANCE',
  'mutuelle': 'INSURANCE',
};

const MUTUELLE_FIELD_MAPPINGS: { productField: string; formField: string }[] = [
  { productField: 'mutuelleHospitalisationHonorairesOptam', formField: 'hospitalisationHonorairesOptam' },
  { productField: 'mutuelleHospitalisationHonorairesNonOptam', formField: 'hospitalisationHonorairesNonOptam' },
  { productField: 'mutuelleHospitalisationChambreParticuliere', formField: 'hospitalisationChambreParticuliere' },
  { productField: 'mutuelleHospitalisationPrimeMaternite', formField: 'hospitalisationPrimeMaternite' },
  { productField: 'mutuelleOptiqueLunettes', formField: 'optiqueLunettes' },
  { productField: 'mutuelleOptiqueLentilles', formField: 'optiqueLentilles' },
  { productField: 'mutuelleOptiquePlafond', formField: 'optiquePlafond' },
  { productField: 'mutuelleOptiqueChirurgieLaser', formField: 'optiqueChirurgieLaser' },
  { productField: 'mutuelleDentaireProthese', formField: 'dentaireProthese' },
  { productField: 'mutuelleDentaireOrthodontieAdulte', formField: 'dentaireOrthodontieAdulte' },
  { productField: 'mutuelleDentaireOrthodontieEnfant', formField: 'dentaireOrthodontieEnfant' },
  { productField: 'mutuellePharmaciePriseEnCharge', formField: 'pharmaciePriseEnCharge' },
  { productField: 'mutuelleMedecinesDouces', formField: 'medecinesDouces' },
  { productField: 'mutuelleConsultationsOptamCas', formField: 'consultationsOptamCas' },
  { productField: 'mutuelleConsultationsNonOptamNonCas', formField: 'consultationsNonOptamNonCas' },
  { productField: 'mutuelleCotisationsMontant', formField: 'cotisationsMontant' },
];

const AE_FIELD_MAPPINGS: { productField: string; formField: string }[] = [
  { productField: 'aeFranchiseIttJours', formField: 'franchiseIttJours' },
  { productField: 'aeTypeIndemnisation', formField: 'typeIndemnisation' },
  { productField: 'aePriseEnChargeDosPsy', formField: 'priseEnChargeDosPsy' },
  { productField: 'aeQuotiteIpt', formField: 'quotiteIpt' },
  { productField: 'aeQuotiteIpp', formField: 'quotiteIpp' },
  { productField: 'aeQuotitePe', formField: 'quotitePe' },
  { productField: 'aeFranchisePeJours', formField: 'franchisePeJours' },
  { productField: 'aeQuotiteDeces', formField: 'quotiteDeces' },
  { productField: 'aeQuotitePtia', formField: 'quotitePtia' },
  { productField: 'aeQuotiteItt', formField: 'quotiteItt' },
];

const FRANCHISE_DELAI_LABELS: Record<string, string> = {
  D_7_0_0: '7/0/0', D_7_7_7: '7/7/7', D_15_3_3: '15/3/3', D_15_15_15: '15/15/15',
  D_30_0_0: '30/0/0', D_30_3_3: '30/3/3', D_30_30_30: '30/30/30',
  D_60_30_30: '60/30/30', D_90_3_3: '90/3/3', D_90_30_30: '90/30/30',
  D_90_60_60: '90/60/60', D_90_90_90: '90/90/90',
};

const DELAI_ATTENTE_LABELS: Record<string, string> = {
  D_0_0: '0/0', D_1_0: '1/0', D_3_0: '3/0', D_3_1: '3/1',
  D_6_0: '6/0', D_6_3: '6/3', D_9_6: '9/6', D_12_0: '12/0',
  D_12_3: '12/3', D_12_6: '12/6', D_12_12: '12/12',
};

const SEUIL_DECLENCHEMENT_LABELS: Record<string, string> = {
  S_15: '15', S_20: '20', S_33: '33', S_50: '50', S_66: '66',
};

const PREVOYANCE_FIELD_MAPPINGS: { productField: string; formField: string; type: 'boolean' | 'enum' | 'direct'; labels?: Record<string, string> }[] = [
  { productField: 'prevoyanceRenfortDos', formField: 'ittRenfortDos', type: 'boolean' },
  { productField: 'prevoyanceAffectionPsy', formField: 'ittAffectionPsy', type: 'boolean' },
  { productField: 'prevoyanceMiTempsTherapeutique', formField: 'ittMiTempsTherapeutique', type: 'boolean' },
  { productField: 'prevoyanceFranchiseDelai', formField: 'ittFranchises', type: 'enum', labels: FRANCHISE_DELAI_LABELS },
  { productField: 'prevoyanceDelaiAttente', formField: 'ittDelaisAttente', type: 'enum', labels: DELAI_ATTENTE_LABELS },
  { productField: 'prevoyanceSeuilDeclenchement', formField: 'invaliditeSeuilDeclenchement', type: 'enum', labels: SEUIL_DECLENCHEMENT_LABELS },
  { productField: 'prevoyanceBaremeInvalidite', formField: 'invaliditeBareme', type: 'direct' },
];

export interface ExistingFile {
  gedDocumentId: number;
  fileIndex: number;
  fileName: string;
}

@UntilDestroy()
@Component({
  selector: 'ea-prevoyance-contrat-form',
  templateUrl: './ea-prevoyance-contrat-form.component.html',
  styleUrls: ['./ea-prevoyance-contrat-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaPrevoyanceContratFormComponent),
    },
  ],
  standalone: false,
})
export class EaPrevoyanceContratFormComponent
  extends PrPrevoyanceContratBuilder
  implements OnInit
{
  type: PrevoyanceType;
  config: (typeof PREVOYANCE_TYPE_CONFIG)[PrevoyanceType];
  productType: string;
  users: any[] = [];
  baremeInvaliditeRef: any[] = [];
  allProducts: { id: number; name: string; partnerId: number; partnerName: string }[] = [];
  allProductsFull: any[] = [];
  providers: { id: number; name: string }[] = [];
  filteredProducts: { id: number; name: string }[] = [];
  selectedProviderId: number | null = null;
  selectedProduct: any = null;
  files: File[] = [];
  existingFiles: ExistingFile[] = [];
  filesToDelete: ExistingFile[] = [];
  filesError: string;
  doneSubject: Subject<void> = new Subject<void>();

  // Callbacks pour création inline (passés à pr-input-autosuggest)
  createProviderFn = (label: string): Observable<any> => {
    return this.productDao.proposeProvider(label, this.productType).pipe(
      map(provider => {
        if (provider?.id && !this.providers.find(p => p.id === provider.id)) {
          this.providers = [...this.providers, { id: provider.id, name: provider.name ?? label }];
        }
        return { id: provider.id, name: provider.name ?? label };
      }),
      catchError(() => {
        this.notificationService.error("Erreur lors de la création de l'assureur.");
        return of(null);
      }),
    );
  };

  createProductFn = (label: string): Observable<any> => {
    if (!this.selectedProviderId) return of(null);
    return this.productDao.proposeProduct(label, this.productType, this.selectedProviderId).pipe(
      map(product => {
        const newProduct = {
          id: product.id,
          name: product.name ?? label,
          partnerId: this.selectedProviderId,
          partnerName: this.providers.find(p => p.id === this.selectedProviderId)?.name ?? '',
        };
        this.allProducts = [...this.allProducts, newProduct];
        this.filteredProducts = this.allProducts
          .filter(p => p.partnerId === this.selectedProviderId)
          .map(p => ({ id: p.id, name: p.name }));
        return { id: product.id, name: product.name ?? label };
      }),
      catchError(() => {
        this.notificationService.error('Erreur lors de la création du produit.');
        return of(null);
      }),
    );
  };

  get isEditing(): boolean {
    return !!this.entity?.id;
  }

  get visibleExistingFiles(): ExistingFile[] {
    return this.existingFiles.filter(
      f =>
        !this.filesToDelete.some(
          d => d.gedDocumentId === f.gedDocumentId && d.fileIndex === f.fileIndex,
        ),
    );
  }

  get hasAnyFile(): boolean {
    return this.files.length > 0 || this.visibleExistingFiles.length > 0;
  }

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private prevoyanceDao: PrevoyanceDao,
    private productDao: ProductDao,
    private documentDao: DocumentDao,
    private httpApi: HttpApiService,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    this.type = this.entity?.type || 'prevoyance';
    this.config = PREVOYANCE_TYPE_CONFIG[this.type];

    if (!this.isAE) {
      this.form.get('cotisationPeriode').setValidators(Validators.required);
      this.form.get('cotisationPeriode').updateValueAndValidity();
    }

    if (this.isPrevoyance) {
      this.prevoyanceDao.getBaremeInvaliditeRef().subscribe(
        ref => (this.baremeInvaliditeRef = ref || []),
      );
    }

    combineLatest([
      this.userDao.getOwners(),
      this.userDao.getChildren(),
    ]).pipe(untilDestroyed(this)).subscribe(([owners, children]) => {
      // Exclure les enfants (relation='OTHER_USER' comme le conjoint).
      // PrUser extends PrOwner → child.relationUser.id === owner.id
      const childIds = new Set(
        (children || []).map((c: any) => Number(c.relationUser?.id)).filter(Boolean),
      );
      this.users = (owners || []).filter(
        o => o.relation !== 'COMPANY' && !o.endDate && !childIds.has(Number(o.id)),
      );
    });

    // Load products for assureur → produit cascade (type depends on prevoyance type)
    this.productType = PRODUCT_TYPE_BY_PREVOYANCE_TYPE[this.type] || 'CONTINGENCY';

    // Load providers separately (includes providers without products)
    this.productDao.getProviders({ productType: this.productType as any }).subscribe(apiProviders => {
      const providerMap = new Map<number, string>();
      // Merge existing providers from products
      this.providers.forEach(p => providerMap.set(p.id, p.name));
      // Add providers from API
      (apiProviders || []).forEach(p => {
        if (p.id && !providerMap.has(p.id)) providerMap.set(p.id, p.name);
      });
      this.providers = Array.from(providerMap.entries()).map(([id, name]) => ({ id, name }));
    });

    this.productDao.getProductsGeneral({ type: [this.productType], enabled: true, size: -1 }).subscribe(products => {
      const filtered = (products || []).filter(p => p.partner?.id && p.partner?.name);
      this.allProductsFull = filtered;
      this.allProducts = filtered
        .map(p => ({ id: p.id, name: p.name, partnerId: p.partner.id, partnerName: p.partner.name }));

      // Merge providers from products into providers list
      const providerMap = new Map<number, string>();
      this.providers.forEach(p => providerMap.set(p.id, p.name));
      this.allProducts.forEach(p => providerMap.set(p.partnerId, p.partnerName));
      this.providers = Array.from(providerMap.entries()).map(([id, name]) => ({ id, name }));

      // Pre-select provider and product when editing
      if (this.entity?.productId) {
        const product = this.allProducts.find(p => p.id === this.entity.productId);
        if (product) {
          const providerObj = this.providers.find(p => p.id === product.partnerId);
          this.form.get('providerId').setValue(providerObj ?? { id: product.partnerId, name: product.partnerName });
          this.onProviderChange(providerObj ?? product.partnerId);
          const productObj = this.filteredProducts.find(p => p.id === this.entity.productId);
          this.form.get('productId').setValue(productObj ?? { id: product.id, name: product.name });
        }
      }
    });

    // Listen to provider changes for cascade
    this.form.get('providerId').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.onProviderChange(value);
    });

    // Listen to product changes for pre-fill
    this.form.get('productId').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      const productId = typeof value === 'object' ? value?.id : value;
      if (!productId) { this.selectedProduct = null; return; }
      const product = this.allProductsFull.find(p => p.id === Number(productId));
      if (!product) { this.selectedProduct = null; return; }
      this.selectedProduct = product;
      if (this.isMutuelle) this.prefillFieldsFromProduct(product, MUTUELLE_FIELD_MAPPINGS);
      else if (this.isAE) this.prefillFieldsFromProduct(product, AE_FIELD_MAPPINGS);
      else if (this.isPrevoyance) this.prefillPrevoyanceFields(product);
    });

    if (this.isEditing) {
      this.loadExistingDocuments();
    }
  }

  private loadExistingDocuments() {
    const gedIds = this.entity.gedDocumentIds || [];
    if (!gedIds.length) return;

    this.documentDao
      .searchDocument(this.entity.accountId, { ids: gedIds })
      .pipe(catchError(() => of([])))
      .subscribe(gedDocs => {
        this.existingFiles = this.buildExistingFiles(gedDocs || [], gedIds);
      });
  }

  private buildExistingFiles(gedDocs: any[], gedIds: number[]): ExistingFile[] {
    const files: ExistingFile[] = [];
    for (const doc of gedDocs) {
      if (!gedIds.includes(Number(doc.id))) continue;
      const docFiles: string[] = doc.files ?? [];
      for (let i = 0; i < docFiles.length; i++) {
        files.push({
          gedDocumentId: Number(doc.id),
          fileIndex: i,
          fileName: this.extractFileName(docFiles[i]),
        });
      }
    }
    return files;
  }

  private extractFileName(filePath: string): string {
    const parts = filePath.split('/');
    let name = parts[parts.length - 1] || filePath;
    const uuidPrefixRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/;
    return name.replace(uuidPrefixRegex, '');
  }

  get title() {
    if (!this.isEditing) return this.config.addLabel;
    const num = this.entity.numeroContrat ? ` (${this.entity.numeroContrat})` : '';
    return `Modifier le contrat "${this.entity.productName ?? ''}${num}"`;
  }

  onProviderChange(providerValue: any) {
    const providerId = typeof providerValue === 'object' ? providerValue?.id : providerValue;
    this.selectedProviderId = providerId ?? null;
    this.filteredProducts = this.allProducts
      .filter(p => p.partnerId === this.selectedProviderId)
      .map(p => ({ id: p.id, name: p.name }));
    // Reset product selection when provider changes
    const currentProductValue = this.form.get('productId').value;
    const currentProductId = typeof currentProductValue === 'object' ? currentProductValue?.id : currentProductValue;
    if (currentProductId) {
      const currentProduct = this.filteredProducts.find(p => p.id === Number(currentProductId));
      if (!currentProduct) {
        this.form.get('productId').setValue(null);
      }
    }
  }

  get isPrevoyance() {
    return this.type === 'prevoyance';
  }

  get isAE() {
    return this.type === 'assurance-emprunteur';
  }

  get isMutuelle() {
    return this.type === 'mutuelle';
  }

  get fraisGenerauxPlaceholder(): string {
    const p = this.selectedProduct;
    if (p?.prevoyanceFraisGenerauxMin != null && p?.prevoyanceFraisGenerauxMax != null) {
      return `${p.prevoyanceFraisGenerauxMin} - ${p.prevoyanceFraisGenerauxMax}`;
    }
    return '';
  }

  get decesPtiaPlaceholder(): string {
    const p = this.selectedProduct;
    if (p?.prevoyanceMontantMin != null && p?.prevoyanceMontantMax != null) {
      return `${p.prevoyanceMontantMin} - ${p.prevoyanceMontantMax}`;
    }
    return '';
  }

  private prefillFieldsFromProduct(product: any, mappings: { productField: string; formField: string }[]) {
    for (const { productField, formField } of mappings) {
      if (product[productField] != null) {
        this.form.get(formField)?.setValue(product[productField]);
      }
    }
  }

  private prefillPrevoyanceFields(product: any) {
    for (const { productField, formField, type, labels } of PREVOYANCE_FIELD_MAPPINGS) {
      const value = product[productField];
      if (value == null) continue;

      if (type === 'boolean') {
        this.form.get(formField)?.setValue(value === true || value === 'true');
      } else if (type === 'enum' && labels) {
        this.form.get(formField)?.setValue(labels[value] ?? value);
      } else {
        this.form.get(formField)?.setValue(value);
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this.files.push(input.files[i]);
      }
      this.filesError = null;
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  markExistingFileForDeletion(file: ExistingFile) {
    this.filesToDelete.push(file);
  }

  viewExistingFile(file: ExistingFile) {
    this.httpApi
      .getWithOptions(
        `/accounts/${this.entity.accountId}/prevoyance/${this.config.path}/contrats/${this.entity.id}/documents/${file.gedDocumentId}/files/${file.fileIndex}`,
        { responseType: 'arraybuffer' },
      )
      .subscribe({
        next: (resp: ArrayBuffer) => {
          const blob = new Blob([resp], { type: 'application/pdf' });
          window.open(URL.createObjectURL(blob), '_blank');
        },
        error: () =>
          this.notificationService.error("Erreur lors de l'ouverture du document."),
      });
  }

  onSubmit() {
    this.displayErrors();
    this.selectErrorInput();

    const missingFiles = !this.hasAnyFile;
    this.filesError = missingFiles ? 'Veuillez joindre au moins un document' : null;

    if (this.form.invalid || missingFiles) return;

    const dto = this.extract();
    this.prFormService.loading();

    const accountId = this.entity.accountId;
    const save$ = this.isEditing
      ? this.prevoyanceDao.updateContrat(accountId, this.type, this.entity.id, dto)
      : this.prevoyanceDao.createContrat(accountId, this.type, dto);

    save$
      .pipe(
        switchMap(contrat => {
          const contratId = contrat.id || this.entity.id;
          const operations = [];

          // Upload nouveaux fichiers
          if (this.files.length > 0) {
            operations.push(this.uploadAndLinkDocuments(accountId, contratId));
          }

          // Supprimer les fichiers marqués (par fichier individuel, pas par gedDocument entier)
          if (this.filesToDelete.length > 0 && this.isEditing) {
            for (const f of this.filesToDelete) {
              operations.push(
                this.prevoyanceDao.removeDocumentFile(
                  accountId, this.type, contratId, f.gedDocumentId, f.fileIndex,
                ).pipe(catchError(() => of(null))),
              );
            }
          }

          return operations.length > 0 ? forkJoin(operations) : of(contrat);
        }),
      )
      .subscribe({
        next: () => {
          this.prFormService.loaded();
          this.doneSubject.next();
          this.notificationService.success('Le contrat a bien été enregistré.');
        },
        error: () => {
          this.prFormService.loaded();
          this.notificationService.error("L'enregistrement a échoué.");
        },
      });
  }

  private uploadAndLinkDocuments(accountId: number, contratId: number) {
    const formData = new FormData();
    this.files.forEach(file => formData.append('documentsFiles', file, file.name));

    const metadata = {
      accountId,
      category: this.config.gedCategory,
      document: this.config.gedDocumentType,
      productCategory: this.config.gedProductCategory,
      label: `${this.config.gedLabelPrefix}-${this.filteredProducts.find(p => p.id === Number(this.form.value.productId))?.name ?? ''}-${this.providers.find(p => p.id === this.selectedProviderId)?.name ?? ''}`,
      reviewed: true,
    };
    formData.append(
      'document',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
    );

    return this.documentDao.saveDocument(formData, accountId).pipe(
      switchMap((gedDoc: any) =>
        this.prevoyanceDao.linkDocumentToContrat(
          accountId, this.type, contratId, gedDoc.id,
        ),
      ),
    );
  }

  onCancel() {
    this.prFormService.close();
  }
}
